import {Injectable} from "@angular/core";
import {Credentials, IcureTokens} from "../models/credentials.model";
import {
  AuthSecretDetails,
  AuthSecretProvider,
  AuthSecretType,
  SmartAuthenticationDetails,
  User
} from "@icure/api";
import {isIcureApiInitialized} from "../helper/icure-api.helper";
import {ApiService} from "./api.service";
import {IcureTokensHelper} from "../helper/icure-tokens.helper";
import {ConfigService} from "./config.service";
import {LocalSettingsService} from "./local-settings.service";

const MAXIMUM_AUTH_SECRET_PROVIDER_RETRIES: number = 2;

interface IcureTokenData {
  g?: string;
  u?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationManagementService {

  constructor(
    private api: ApiService,
    private localSettings: LocalSettingsService,
    private configService: ConfigService
  ) {
  }

  public async initializeAndConfigureIcureBasicApi(
    credentials: Credentials | undefined = undefined
  ): Promise<User | undefined> {
    if (isIcureApiInitialized(this.api)) return await this.api.userApi.getCurrentUser();

    const authenticationOptions: SmartAuthenticationDetails | undefined = this.getAuthenticationOptions(credentials);
    if (!authenticationOptions) return undefined;

    await this.api.reconfigure(this.configService.icureHost, authenticationOptions);

    // Not logged in yet
    if (!isIcureApiInitialized(this.api)) return undefined;

    // Get iCureTokens && put in session storage, since we're connected
    await this.getIcureTokensFromApiAndSaveInSessionStorage();

    // Get the one
    const currentUser: User = await this.api.userApi.getCurrentUser();

    // Try getting groupId (and save it when found) from any available source (credentials, current user, session storage, electron, or tokens)
    await this.getAndSaveGroupId(currentUser, credentials);

    return currentUser;
  }

  private getGroupSelectorCallback(
    groupSelectorCallback: (() => Promise<string>) | null = null,
  ): (() => Promise<string>) | null {
    if (groupSelectorCallback) return groupSelectorCallback;

    const iCureGroupId: string | undefined = IcureTokensHelper.getGroupId();
    return !iCureGroupId ? groupSelectorCallback : () => Promise.resolve(iCureGroupId);
  }

  private async getAndSaveGroupId(currentUser?: User | undefined, credentials?: Credentials | undefined): Promise<string | undefined> {
    return IcureTokensHelper.saveGroupId(
      currentUser?.groupId ||
      credentials?.groupId ||
      IcureTokensHelper.getGroupId() ||
      this.parseIcureTokenData(IcureTokensHelper.getRefreshTokenCloud())?.g ||
      this.parseIcureTokenData(IcureTokensHelper.getTokenCloud())?.g ||
      this.parseIcureTokenData(IcureTokensHelper.getRefreshToken())?.g ||
      this.parseIcureTokenData(IcureTokensHelper.getToken())?.g
    );
  }

  private async getIcureTokensFromApiAndSaveInSessionStorage(): Promise<IcureTokens | undefined> {
    try {
      const iCureTokens: IcureTokens | undefined = (await this.api?.authApi?.authenticationProvider?.getIcureTokens()) || undefined;
      IcureTokensHelper.saveTokensCloud(iCureTokens);
      return IcureTokensHelper.saveTokens(iCureTokens);
    } catch (e) {
      console.error('Failed to obtain authentication tokens', e);
      return undefined;
    }
  }

  private getAuthenticationOptions(
    credentials: Credentials | undefined = undefined,
    isCloudApi: boolean = false,
  ): SmartAuthenticationDetails | undefined {
    return (
      this.getAuthenticationOptionsUsingIcureTokens(credentials, isCloudApi) ||
      this.getAuthenticationOptionsUsingCredentials(credentials)
    );
  }

  private getAuthenticationOptionsUsingCredentials(
    credentials: Credentials | undefined,
  ): SmartAuthenticationDetails | undefined {
    if (!credentials?.password || (!credentials?.username && !(credentials?.groupId && credentials?.userId))) return undefined;
    return {
      username: IcureTokensHelper.saveUsername(credentials.getFullUsername()),
      secretProvider: new AuthSecretProviderImplementation(
        this.api,
        this.localSettings
      ),
      initialSecret: {password: credentials.password},
    } as SmartAuthenticationDetails;
  }

  private getAuthenticationOptionsUsingIcureTokens(
    credentials: Credentials | undefined,
    isCloudApi: boolean = false,
  ): SmartAuthenticationDetails | undefined {
    // prettier-ignore
    const iCureTokens: IcureTokens | undefined = this.getIcureTokensFromCredentialsOrFromSessionStorage(credentials, isCloudApi);
    return !iCureTokens?.refreshToken
      ? undefined
      : ({
        username: IcureTokensHelper.getUserName() || this.getFullUsernameFromRefreshToken(iCureTokens.refreshToken),
        secretProvider: new AuthSecretProviderImplementation(
          this.api,
          this.localSettings
        ),
        initialRefreshToken: iCureTokens?.refreshToken,
      } as SmartAuthenticationDetails);
  }

  private getIcureTokensFromCredentialsOrFromSessionStorage(
    credentials: Credentials | undefined,
    isCloudApi: boolean = false,
  ): IcureTokens | undefined {
    return this.getIcureTokensFromCredentials(credentials) || (!isCloudApi ? IcureTokensHelper.getTokens() : IcureTokensHelper.getTokensCloud());
  }

  private getIcureTokensFromCredentials(credentials?: Credentials | undefined): IcureTokens | undefined {
    return credentials?.icureToken?.token && credentials?.icureToken?.refreshToken ? credentials.icureToken : undefined;
  }

  private getFullUsernameFromRefreshToken(refreshToken: string): string | undefined {
    const refreshTokenData: IcureTokenData = this.parseIcureTokenData(refreshToken);
    return !refreshTokenData?.g && !refreshTokenData?.u ?
      undefined :
      !refreshTokenData?.g ?
        `${refreshTokenData.u}` :
        `${refreshTokenData.g}/${refreshTokenData.u}`
      ;
  }

  private parseIcureTokenData(token: string | undefined): IcureTokenData {
    let tokenData: IcureTokenData = {};
    try {
      tokenData = JSON.parse(atob((token || '').split('.')[1]));
    } catch {
      tokenData = {};
    }
    return tokenData;
  }
}


export class AuthSecretProviderImplementation implements AuthSecretProvider {
  constructor(
    private api: ApiService,
    private localSettings: LocalSettingsService
  ) {
  }

  public async getSecret(
    acceptedSecrets: AuthSecretType[],
    previousAttempts: AuthSecretDetails[],
  ): Promise<AuthSecretDetails> {
    if ((previousAttempts || []).length > MAXIMUM_AUTH_SECRET_PROVIDER_RETRIES) throw new Error('Invalid password');

    if (!acceptedSecrets.includes(AuthSecretType.PASSWORD)) throw new Error('Password not accepted as AuthSecretType');

    const currentUser: User = await this.api?.userApi?.getCurrentUser(true);
    if (!currentUser?.id) throw new Error('Not connected');

    return {
      value: this.localSettings.basicAuth?.split(':')[0],
      secretType: AuthSecretType.PASSWORD,
    } as AuthSecretDetails;
  }
}
